type HTMLTag = keyof HTMLElementTagNameMap;

export type MarkdownNode = {
	tag?: HTMLTag;
	text?: string;
	depth: number;
	children: MarkdownNode[],
};

export class Markdown {

	private readonly selfClosingTags: HTMLTag[] = [ "br", "img", "input" ];
	private readonly unformattedNodes: HTMLTag[] = [ "code", "pre" ];

	constructor(public readonly classes: Partial<Record<HTMLTag, string>> = { }) { }

	private treeToHtml(node: MarkdownNode, formatContents: boolean = true): string {
		let contents: string = "";
		if (node.text) {
			contents += formatContents ? this.formatMarkdownNode(node.text.trim()) : node.text.trim();
		}
		formatContents = formatContents && (!node.tag || this.unformattedNodes.indexOf(node.tag) === -1);
		contents += node.children.map(child => this.treeToHtml(child, formatContents)).join("\n");

		if (node.tag) {
			return this.formatTag(node.tag, contents);
		}
		return contents;
	}

	private formatMarkdownNode(text: string): string {
		return text
			.replace(/\*\*(.*?)\*\*/, this.formatTag("strong", "$1"))
			.replace(/__(.*?)__/, this.formatTag("strong", "$1"))
			.replace(/\*(.*?)\*/, this.formatTag("em", "$1"))
			.replace(/_(.*?)_/, this.formatTag("em", "$1"))
			.replace(/`(.*?)`/, this.formatTag("code", "$1"))
			.replace(/(!)?\[(.*?)]\((.*)\)/, (substr, isImage, content, url) => {
				if (isImage) {
					return this.formatTag("img", "", ` src="${url}" alt="${content}"`);
				}
				return this.formatTag("a", content, ` href="${url}"`);
			});
	}

	private formatTag(tag: HTMLTag, contents?: string, attributes: string = ""): string {
		const classAttribute = tag in this.classes ? ` class="${this.classes[tag]}"` : "";
		if (this.selfClosingTags.indexOf(tag) !== -1 && !contents) {
			return `<${tag}${classAttribute}${attributes} />`;
		}
		return `<${tag}${classAttribute}${attributes}>${contents}</${tag}>`;

	}

	public lex(text: string): MarkdownNode {
		let newlineCount = 0;
		let inCodeBlock = false;
		let tableNode: MarkdownNode|undefined;
		const lines = text.trim().replace("\r", "").split("\n");
		const root: MarkdownNode = { depth: -1, children: [] };

		lines.forEach(line => {
			if (line.trim().length === 0) {
				newlineCount++;
				return;
			}

			const listMatch = line.match(/^([\s]*)(-|\*|\+|[\d]+\.)\s+(.*?)$/);
			if (listMatch) {
				const [ , listDepthString, item, content] = listMatch;
				const depth = listDepthString.length;
				let parent = root;
				const tag = item.match(/[\s]+\./) ? "ol" : "ul";
				while (true) {
					if (parent.children.length === 0) {
						parent.children.push({ tag, depth, children: [ ]});
						parent = parent.children[parent.children.length - 1];
						break;
					}
					const last = parent.children[parent.children.length - 1];
					if (depth > last.depth + 1) {
						parent = last;
						continue;
					}
					if (last.tag != tag) {
						parent.children.push({ tag, depth, children: [] });
					}
					parent = parent.children[parent.children.length - 1];
					break;
				}
				parent.children.push({ tag: "li", depth, children: [ { depth: depth + 1, text: content, children: [] }] });
				newlineCount = 0;
				return;
			}

			const tableMatch = line.match(/^\s*\|\s*(.*?)\s*\|\s*$/);
			if (tableMatch) {
				if (!tableNode) {
					tableNode = {
						tag:      "table",
						depth:    0,
						text:     "",
						children: [
							{ tag: "thead", depth: 1, text: "", children: [] },
							{ tag: "tbody", depth: 1, text: "", children: [] },
						],
					};
					root.children.push(tableNode);
				}

				const cells = tableMatch[1].split("|").map(s => s.trim());
				if (cells[0].includes("---") && tableNode.children[1].children) {
					tableNode.children[0].children = tableNode.children[1].children.map(tr => {
						tr.children = tr.children.map(td => ({ ...td, tag: "th" }));
						return tr;
					});
					tableNode.children[1].children = [];
				} else {
					tableNode.children[1].children.push({
						tag:      "tr",
						depth:    2,
						text:     "",
						children: cells.map(str => ({
							tag:      "td",
							depth:    3,
							text:     str,
							children: []
						}))
					});
				}
				return;
			} else if (tableNode) {
				tableNode = undefined;
			}

			const headerMatch = line.match(/\s*([#]+)\s*(.*?)$/);
			if (headerMatch) {
				const tag = "h" + headerMatch[1].length as "h1"|"h2"|"h3"|"h4"|"h5"|"h6";
				root.children.push({ tag, depth: 0, children: [ { depth: 1, text: headerMatch[2], children: [] } ]});
				newlineCount = 0;
				return;
			}

			const codeMatch = line.match(/^```/);
			if (codeMatch) {
				newlineCount = 0;
				if (!inCodeBlock) {
					root.children.push({ tag: "code", depth: 0, children: [ ]});
					inCodeBlock = true;
				} else {
					inCodeBlock = false;
				}
				return;
			}

			const quoteMatch = line.match(/^((> )+)(.*?)$/);
			if (quoteMatch) {
				const [ , quoteDepthString, , content ] = quoteMatch;
				const depth = Math.floor(quoteDepthString.length / 2) - 1;
				let parent = root;
				const tag = "blockquote";
				while (true) {
					if (parent.children.length === 0) {
						parent.children.push({ tag, depth, children: [ ] });
						parent = parent.children[parent.children.length - 1];
						break;
					}
					const last = parent.children[parent.children.length - 1];
					if (depth > last.depth) {
						parent = last;
						continue;
					}
					if (last.tag != tag) {
						parent.children.push({ tag, depth, children: [] });
					}
					parent = parent.children[parent.children.length - 1];
					break;
				}
				parent.children.push({ depth, text: content + this.formatTag("br"), children: [] });
				newlineCount = 0;
				return;
			}

			const lastNode = root.children[ root.children.length - 1];
			if (inCodeBlock) {
				lastNode.children.push({ depth: 1, text: line, children: [ ] });
			} else if (!lastNode || lastNode.tag != "p" || newlineCount > 0) {
				root.children.push({ tag: "p", depth: 0, children: [ { depth: 1, text: line, children: [] } ]});
				newlineCount = 0;
				return;
			} else {
				lastNode.children.push({ tag: "br", depth: 0, children: [ ]});
				lastNode.children.push({ depth: 1, text: line, children: [ ]});
				newlineCount = 0;
			}
		});

		return root;
	}

	public toHTML(markdown: string): string {
		return this.treeToHtml(this.lex(markdown));
	}

	public toNode(markdown: string): Node {
		const root = document.createElement("div");
		root.innerHTML = this.toHTML(markdown);
		if (root.childNodes.length === 1) {
			return root.childNodes[0];
		}
		return root;
	}
}
